import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  Landmark,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { DashboardKpis } from "../../../api/dashboard";
import { formatMinor } from "../../../lib/money";

interface AccountBalanceTilesProps {
  kpis: DashboardKpis | null;
  isLoading: boolean;
}

interface BalanceTileConfig {
  key: keyof Pick<
    DashboardKpis,
    "cashBalanceMinor" | "bankBalanceMinor" | "receivablesMinor" | "payablesMinor"
  >;
  titleKey: string;
  hintKey: string;
  icon: ReactNode;
  accentClass: string;
  iconClass: string;
}

function BalanceTile({
  title,
  hint,
  amount,
  icon,
  accentClass,
  iconClass,
}: {
  title: string;
  hint: string;
  amount: string;
  icon: ReactNode;
  accentClass: string;
  iconClass: string;
}) {
  return (
    <article className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${accentClass}`}
        aria-hidden="true"
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </p>
          <p className="mt-1 text-xl font-bold tracking-tight text-godo-navy" dir="ltr">
            {amount}
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-slate-500">{hint}</p>
        </div>

        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClass}`}
        >
          {icon}
        </div>
      </div>
    </article>
  );
}

export function AccountBalanceTiles({ kpis, isLoading }: AccountBalanceTilesProps) {
  const { t } = useTranslation();

  const tiles: BalanceTileConfig[] = [
    {
      key: "cashBalanceMinor",
      titleKey: "dashboard.cash_on_hand",
      hintKey: "dashboard.cash_on_hand_hint",
      icon: <Wallet className="h-6 w-6" aria-hidden="true" />,
      accentClass: "bg-emerald-500",
      iconClass: "bg-emerald-50 text-emerald-700",
    },
    {
      key: "bankBalanceMinor",
      titleKey: "dashboard.bank_balances",
      hintKey: "dashboard.bank_balances_hint",
      icon: <Landmark className="h-6 w-6" aria-hidden="true" />,
      accentClass: "bg-sky-500",
      iconClass: "bg-sky-50 text-sky-700",
    },
    {
      key: "receivablesMinor",
      titleKey: "dashboard.receivables",
      hintKey: "dashboard.receivables_hint",
      icon: <ArrowDownLeft className="h-6 w-6" aria-hidden="true" />,
      accentClass: "bg-indigo-500",
      iconClass: "bg-indigo-50 text-indigo-700",
    },
    {
      key: "payablesMinor",
      titleKey: "dashboard.payables",
      hintKey: "dashboard.payables_hint",
      icon: <ArrowUpRight className="h-6 w-6" aria-hidden="true" />,
      accentClass: "bg-amber-500",
      iconClass: "bg-amber-50 text-amber-700",
    },
  ];

  if (isLoading) {
    return (
      <section aria-label={t("dashboard.araseda_section")}>
        <div className="mb-1 flex items-center gap-1.5">
          <Banknote className="h-4 w-4 text-godo-navy" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900">
            {t("dashboard.araseda_section")}
          </h2>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-lg border border-slate-200 bg-slate-100"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!kpis) {
    return (
      <section aria-label={t("dashboard.araseda_section")}>
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          {t("dashboard.kpis_unavailable")}
        </div>
      </section>
    );
  }

  return (
    <section aria-label={t("dashboard.araseda_section")}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-1">
        <div className="flex items-center gap-1.5">
          <Banknote className="h-4 w-4 text-godo-navy" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900">
            {t("dashboard.araseda_section")}
          </h2>
          <span className="text-[10px] text-slate-400">{t("dashboard.realtime_balances")}</span>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map((tile) => (
          <BalanceTile
            key={tile.key}
            title={t(tile.titleKey)}
            hint={t(tile.hintKey)}
            amount={formatMinor(kpis[tile.key])}
            icon={tile.icon}
            accentClass={tile.accentClass}
            iconClass={tile.iconClass}
          />
        ))}
      </div>
    </section>
  );
}
